/**
 * The run list: every plan the planner has written, newest first as the API
 * sends them, each opening on its first planned day.
 *
 * Not paginated. Coding standards 11.10 asks a register to paginate on the
 * server, and `GET /api/runs` takes no page parameters: a run is one planning
 * horizon, there are a dozen of them, and the list is the API's whole answer.
 * The day an endpoint pages them, this table gets a pager.
 */
import { PageHeader, Section, Table } from "@depicomnlbv/platform-ui";
import { Link } from "react-router-dom";

import { useRuns, type Run } from "../api";
import { FailureSentence } from "../components/Failure";
import { runStatus } from "../runs";
import { formatShortDate } from "../time";
import { useTitle } from "../useTitle";

function RunRow({ run }: { run: Run }) {
  const first = run.first_route_date;
  return (
    <tr>
      <th scope="row">
        {first ? (
          <Link to={`/runs/${run.run_id}/days/${first}`}>Run {run.run_id}</Link>
        ) : (
          <span>Run {run.run_id}</span>
        )}
      </th>
      <td>
        {formatShortDate(run.horizon_start)} to {formatShortDate(run.horizon_end)}
      </td>
      <td>{runStatus(run.status)}</td>
      <td className="dpc-table__num">{run.route_count}</td>
      <td>{first ? formatShortDate(first) : "No routes"}</td>
      <td>{run.last_route_date ? formatShortDate(run.last_route_date) : "No routes"}</td>
    </tr>
  );
}

export function Runs() {
  useTitle("Planning runs");
  const runs = useRuns();

  return (
    <>
      <PageHeader
        qualifier="Every plan the planner has written. A run opens on its first planned day."
        title="Planning runs"
      />
      <Section
        empty={runs.data?.runs.length === 0 ? "There are no planning runs yet." : undefined}
        error={runs.isError ? <FailureSentence error={runs.error} /> : undefined}
        flush
        loading={runs.isPending}
        title="Runs, newest first"
      >
        {runs.data ? (
          // Six columns do not fit a phone; they scroll in the table's own
          // box and the page does not (WCAG 1.4.10).
          <Table caption="Planning runs, newest first" captionHidden>
            <thead>
              <tr>
                <th scope="col">Run</th>
                <th scope="col">Horizon</th>
                <th scope="col">Status</th>
                <th className="dpc-table__num" scope="col">
                  Routes
                </th>
                <th scope="col">First route</th>
                <th scope="col">Last route</th>
              </tr>
            </thead>
            <tbody>
              {runs.data.runs.map((run) => (
                <RunRow key={run.run_id} run={run} />
              ))}
            </tbody>
          </Table>
        ) : null}
      </Section>
    </>
  );
}
